import { BtnOutline } from '../primitives/BtnOutline';
import { CollapsibleCard } from '../primitives/CollapsibleCard';
import { FadeUp } from '../primitives/FadeUp';
import { SectionH2 } from '../primitives/SectionH2';
import { SectionLabel } from '../primitives/SectionLabel';
import { useIsMobile } from '../../hooks/useIsMobile';
import { TEXT_SECONDARY } from '../../tokens';

interface PainPoint {
  title: string;
  desc: string;
}

const PAIN_POINTS: readonly PainPoint[] = [
  {
    title: 'A lead that sits an hour is worth a fraction of what it was worth at minute one',
    desc: 'Contact odds drop more than tenfold within the first hour of a lead coming in, and the odds of qualifying that lead drop sharply between the 5 and 30 minute mark. The lead did not change. Only the time did. (Oldroyd, MIT Sloan / InsideSales.com, 2007)',
  },
  {
    title: 'Waiting does not just cost you the lead, it changes how they see you',
    desc: 'Temporal distance changes how a mind processes the same task, so a lead sitting a few minutes flips from concrete and urgent to abstract and can-wait, both for the prospect and for how they now read your business. (Construal Level Theory, Trope and Liberman, 2010)',
  },
  {
    title: '97% of people read reviews before they choose a local business',
    desc: 'When someone has no direct evidence of their own, they treat other people’s judgments as evidence about reality. Reviews are not a nice-to-have sitting at the end of the job, they are the thing being read before anyone contacts you. (BrightLocal, 2026 edition)',
  },
  {
    title: 'Doing it yourself costs 15 to 20 hours a week, and nothing is systematised when you stop',
    desc: 'That is most of a working day, every week, spent drafting, resizing, scheduling and chasing. The moment you get busy with actual client work, the content stops, and the compounding starts again from zero.',
  },
  {
    title: 'An agency runs $42K to $96K a year and you own nothing at the end of it',
    desc: 'You still spend 3 to 8 hours a week briefing, reviewing and sitting on status calls, and when the retainer ends the files, the history and the systems stay with them. An in-house hire runs $211K to $320K fully loaded. (AU 2026 benchmarks)',
  },
];

export function ProblemSection() {
  const isMobile = useIsMobile();

  return (
    <section
      id="problem"
      style={{ padding: isMobile ? '60px 20px' : '130px 64px', background: 'rgba(8,8,8,0.88)' }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1.2fr',
          gap: isMobile ? 40 : 80,
          alignItems: 'start',
        }}
      >
        <FadeUp>
          <SectionLabel number="01">The Problem</SectionLabel>
          <SectionH2 style={{ marginBottom: 24 }}>The gap is not effort. It is consistency.</SectionH2>
          <p
            style={{
              color: TEXT_SECONDARY,
              fontSize: '1.05rem',
              lineHeight: 1.75,
              maxWidth: 460,
              marginBottom: 32,
            }}
          >
            Every lead that sits, every job that finishes without a review being asked for, every week
            the posting stops because you got busy, that is money leaving quietly. None of it shows up
            on a P&amp;L, which is exactly why it keeps happening.
          </p>
          <BtnOutline href="#solution">See The Solution →</BtnOutline>
        </FadeUp>

        <div style={{ display: 'grid', gap: 10 }}>
          {PAIN_POINTS.map((card, idx) => (
            <FadeUp key={card.title} delay={idx * 60}>
              <CollapsibleCard title={card.title}>{card.desc}</CollapsibleCard>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
